import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import CurrencyInput from '../../ui/inputs/CurrencyInput';
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox';
import Select from '../../ui/selects/Select';
import Textarea from '../../ui/inputs/Textarea';
import { LineChart, X } from 'lucide-react';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { useToast } from '../../ui/toast/ToastContext';

const MODALITY_OPTIONS = [
  { label: 'Nacional', value: 'national' },
  { label: 'Internacional', value: 'international' },
];

const FINANCIAL_TYPE_OPTIONS = {
  national: [
    { label: 'Renda Fixa - Pós', value: 'Renda Fixa - Pós' },
    { label: 'Renda Fixa - Pré', value: 'Renda Fixa - Pré' },
    { label: 'Renda Fixa - IPCA+', value: 'Renda Fixa - IPCA+' },
    { label: 'Renda Fixa - Internacional', value: 'Renda Fixa - Internacional' },
    { label: 'Multimercado', value: 'Multimercado' },
    { label: 'Ações - Brasil', value: 'Ações - Brasil' },
    { label: 'Ações - Internacional', value: 'Ações - Internacional' },
    { label: 'Fundos Imobiliários', value: 'Fundos Imobiliários' },
    { label: 'Alternativos', value: 'Alternativos' },
  ],
  international: [
    { label: 'Renda Fixa Longo Prazo', value: 'Renda Fixa Longo Prazo' },
    { label: 'Renda Fixa Curto Prazo', value: 'Renda Fixa Curto Prazo' },
    { label: 'REITs', value: 'REITs' },
    { label: 'Commodities', value: 'Commodities' },
    { label: 'Ações', value: 'Ações' },
    { label: 'Caixa', value: 'Caixa' },
  ],
};

const initialForm = {
  name: '',
  value: '',
  isPersonal: false,
  notes: '',
  cnpj: '',
  code: '',
  isinCode: '',
  quantity: '',
  averagePrice: '',
  grossValue: '',
  modality: 'national',
  financialType: '',
};

const FinancialModal = ({ clientId, financialId = null, scope, onClose, onSuccess }) => {
  const isEdit = !!financialId;
  const { addToast } = useToast();

  const [form, setForm] = useState(initialForm);

  const financialTypeOptions = useMemo(() => {
    return FINANCIAL_TYPE_OPTIONS[form.modality] || [];
  }, [form.modality]);

  useEffect(() => {
    if (!isEdit) return;

    fetch(`/app/planner/client/${clientId}/dashboard/properties/financials/${financialId}?scope=${scope}`)
      .then((res) => res.json())
      .then((data) => {
        const camelData = camelizeKeys(data);

        setForm({
          ...initialForm,
          ...camelData,
          modality: camelData.modality || 'national',
        });
      })
      .catch((err) => {
        console.error('Erro ao carregar saldo/investimento', err);
        addToast({ type: 'error', message: 'Erro ao carregar dados do saldo/investimento' });
      });
  }, [isEdit, clientId, financialId, scope, addToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleModalityChange = (e) => {
    const { value } = e.target;
    const nextOptions = FINANCIAL_TYPE_OPTIONS[value] || [];

    setForm((prev) => {
      const currentFinancialTypeIsValid = nextOptions.some((option) => option.value === prev.financialType);

      return {
        ...prev,
        modality: value,
        financialType: currentFinancialTypeIsValid ? prev.financialType : '',
      };
    });
  };

  const handleCheckbox = (name) => {
    setForm((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = snakecaseKeys({ financialProperty: form });

    const url = isEdit
      ? `/app/planner/client/${clientId}/dashboard/properties/financials/${financialId}?scope=${scope}`
      : `/app/planner/client/${clientId}/dashboard/properties/financials?scope=${scope}`;

    const method = isEdit ? 'PATCH' : 'POST';

    const successMsg = isEdit
      ? 'Saldo/Investimento atualizado com sucesso!'
      : 'Saldo/Investimento cadastrado com sucesso!';

    try {
      const res = await fetchWithCsrf(url, {
        method,
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao salvar saldo/investimento');
      }

      onClose();
      addToast({ type: 'success', message: successMsg });
      onSuccess();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <LineChart size={24} className="text-tealMedium" strokeWidth={1.25} />
            {isEdit ? 'Editar Saldo/Investimento' : 'Cadastrar Saldo/Investimento'}
          </div>

          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <Input name="cnpj" label="Nome" value={form.cnpj} onChange={handleChange} />

          <Input name="code" label="Código de Negociação" value={form.code} onChange={handleChange} />

          <Input type="number" name="quantity" label="Quantidade" value={form.quantity} onChange={handleChange} />

          <CurrencyInput name="grossValue" label="Valor Total" value={form.grossValue} onChange={handleChange} />

          <Select
            name="modality"
            label="Origem"
            value={form.modality}
            onChange={handleModalityChange}
            options={MODALITY_OPTIONS}
          />

          <Select
            name="financialType"
            label="Tipo do Saldo/Investimento"
            value={form.financialType}
            onChange={handleChange}
            options={financialTypeOptions}
          />

          <Textarea
            name="notes"
            label="Observações"
            value={form.notes}
            onChange={handleChange}
            className="md:col-span-2"
            rows={4}
          />

          <CustomCheckbox
            name="isPersonal"
            label="É um bem particular"
            checked={form.isPersonal}
            onChange={() => handleCheckbox('isPersonal')}
            labelClassName="text-base"
          />

          <div className="md:col-span-2 flex justify-end items-center mt-1">
            <Button type="submit">{isEdit ? 'SALVAR ALTERAÇÕES' : 'CADASTRAR SALDO/INVESTIMENTO'}</Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default FinancialModal;
